import type { ReactNode } from 'react';
import { ALIGNMENT_LABELS, ALIGNMENTS, ICON_POSITION_LABELS, ICON_POSITIONS, NONE, type Alignment, type IconPosition, type TileStyle } from '@casa/model';
import type { ResolvedTileStyle } from '@casa/tiles';
import { ColorField } from '../forms/color_fields.tsx';
import { ChoiceField, TextFieldRow, ToggleRow } from '../forms/form_rows.tsx';
import { SettingsGroup } from '../settings/settings_ui.tsx';
import { GaugeStyleRow } from './gauge_style_picker.tsx';

// A tile's or the dashboard's style (dashboard-editor spec §5.9, TS-1, TS-4; the shadow §5.11, DS-5):
// each property left empty takes the one below it, shown as its default (the dashboard's, else the
// defaults).

/** The legacy text colour, where neither the tile nor the dashboard sets one (T-2). */
const LEGACY_TEXT = '#ffffff';

/** A colour as a default to show: "none" is shown as the absence of one. */
const shownColor = (color: string | undefined) : string | undefined => (color === NONE ? undefined : color);

type SizeKey = 'padding' | 'radius' | 'borderWidth' | 'titleHeight' | 'iconSize' | 'iconRadius' | 'iconMargin' | 'shadowBlur';

const SIZES: readonly { key: SizeKey; label: string }[] = [
    { key: 'padding', label: 'Padding' },
    { key: 'radius', label: 'Corner radius' },
    { key: 'borderWidth', label: 'Border width' },
    { key: 'titleHeight', label: 'Title height' },
];

export interface TileStyleFieldsProps {
    /** What is set here; a property left out takes `fallback`'s. */
    style: TileStyle;
    /** What a property left out comes to. */
    fallback: ResolvedTileStyle;
    /** The colours the dashboard already uses. */
    known: readonly string[];
    /** Whether to offer the text colour: not for a group, which has no text. */
    hasText: boolean;
    /** Whether to offer the gauge's style (docs/specs/gauge-styles.md): the dashboard's, the default for every gauge; a tile's is in its gauge's options (issue #24). */
    hasGauge?: boolean;
    /** Whether to offer the value's alignment (TS-15): a value tile's, and the dashboard's for every value tile. */
    hasValue?: boolean;
    onChange: (style: TileStyle) => void;
}

export function TileStyleFields({ style, fallback, known, hasText, hasGauge = false, hasValue = false, onChange }: TileStyleFieldsProps) : ReactNode {
    const set = <K extends keyof TileStyle>(key: K, value: TileStyle[K] | undefined) : void => {
        const { [key]: _old, ...rest } = style;
        onChange(value === undefined ? rest : { ...rest, [key]: value });
    };
    const size = (key: SizeKey, label: string) : ReactNode => (
        <TextFieldRow
            key={key}
            label={label}
            type="number"
            value={style[key] === undefined ? undefined : String(style[key])}
            placeholder={`Default (${fallback[key]})`}
            onChange={(typed) => {
                const number = typed === undefined ? undefined : Number(typed);
                if (number === undefined || (Number.isFinite(number) && number >= 0)) {
                    set(key, number);
                }
            }}
        />
    );
    // The shadow's offset: below 0 is to the left or upwards.
    const offset = (key: 'shadowX' | 'shadowY', label: string) : ReactNode => (
        <TextFieldRow
            key={key}
            label={label}
            type="number"
            value={style[key] === undefined ? undefined : String(style[key])}
            placeholder={`Default (${fallback[key]})`}
            onChange={(typed) => {
                const number = typed === undefined ? undefined : Number(typed);
                if (number === undefined || Number.isFinite(number)) {
                    set(key, number);
                }
            }}
        />
    );
    const alignment = (key: 'titleAlign' | 'valueAlign' | 'captionAlign', label: string) : ReactNode => {
        // The caption's has no alignment of its own by default: it follows the value's (SR-9).
        const shown = key === 'captionAlign' ? (fallback.captionAlign === undefined ? 'as the value' : ALIGNMENT_LABELS[fallback.captionAlign]) : ALIGNMENT_LABELS[fallback[key]];
        return (
            <ChoiceField
                label={label}
                options={ALIGNMENTS.map((align) => ({ value: align, label: ALIGNMENT_LABELS[align] }))}
                value={style[key]}
                onChange={(chosen) => set(key, chosen as Alignment | undefined)}
                isOptional
                noneLabel={`Default (${shown})`}
                placeholder={`Default (${shown})`}
            />
        );
    };
    const color = (key: 'background' | 'border' | 'titleBackground' | 'title' | 'text' | 'iconColor' | 'iconBackground' | 'shadowColor' | 'captionColor', label: string, description?: string) : ReactNode => (
        <ColorField
            label={label}
            description={description}
            value={style[key]}
            fallback={key === 'text' ? (fallback.text ?? LEGACY_TEXT) : key === 'iconColor' ? (fallback.iconColor ?? style.title ?? fallback.title) : shownColor(fallback[key])}
            // "none" where it means something (TS-6): no background, no border, no title background.
            none={key === 'title' || key === 'text' || key === 'iconColor' || key === 'shadowColor' || key === 'captionColor' ? undefined : NONE}
            known={known}
            onChange={(value) => set(key, value)}
        />
    );

    return (
        <>
            <SettingsGroup title="Colours">
                <ToggleRow
                    label="Background, border and shadow"
                    detail={`Off: none of them, whatever they are set to; for a tile laid over others. Default: ${fallback.showBackground ? 'on' : 'off'}`}
                    checked={style.showBackground ?? fallback.showBackground}
                    // As the capitals: kept only where it differs from the default (DS-8).
                    onChange={(checked) => set('showBackground', checked === fallback.showBackground ? undefined : checked)}
                />
                {color('background', 'Background')}
                {color('border', 'Border')}
                {color('title', 'Title', 'The title, and its icon unless that has a colour of its own.')}
                {color('titleBackground', 'Title background', 'Fills the title row out to the edges of the tile.')}
                {hasText ? color('text', 'Text', 'Text without a colour of its own (a light\'s on and off, a value\'s colour win).') : null}
            </SettingsGroup>
            {hasGauge ? (
                <SettingsGroup title="Gauge" note="The gauge's own options (colours, thresholds, look) are in the tile's options.">
                    <GaugeStyleRow value={style.gaugeStyle} fallback={fallback.gaugeStyle} onChange={(gaugeStyle) => set('gaugeStyle', gaugeStyle)} />
                </SettingsGroup>
            ) : null}
            {hasValue ? (
                <SettingsGroup title="Value" note="A value tile's number and its unit (a gauge stays in the middle), and its caption below them.">
                    {alignment('valueAlign', 'Alignment')}
                    <TextFieldRow
                        label="Caption size"
                        type="number"
                        value={style.captionSize === undefined ? undefined : String(style.captionSize)}
                        placeholder={`Default (${fallback.captionSize})`}
                        detail="In percent of the title height."
                        onChange={(typed) => {
                            const number = typed === undefined ? undefined : Number(typed);
                            if (number === undefined || (Number.isFinite(number) && number >= 10 && number <= 400)) {
                                set('captionSize', number);
                            }
                        }}
                    />
                    {color('captionColor', 'Caption colour')}
                    {alignment('captionAlign', 'Caption alignment')}
                </SettingsGroup>
            ) : null}
            <SettingsGroup title="Title">
                {alignment('titleAlign', 'Alignment')}
                <ToggleRow
                    label="Title in capitals"
                    detail={`Default: ${fallback.titleUppercase ? 'on' : 'off'}`}
                    checked={style.titleUppercase ?? fallback.titleUppercase}
                    // Kept only where it differs from the default, so a tile follows the dashboard otherwise.
                    onChange={(checked) => set('titleUppercase', checked === fallback.titleUppercase ? undefined : checked)}
                />
            </SettingsGroup>
            <SettingsGroup title="Icon" note="The tile's icon (chosen in the tile's dialog), drawn over the title and the content, the padding and its margin from the tile's edges. Sizes in pixels.">
                <ChoiceField
                    label="Position"
                    options={ICON_POSITIONS.map((position) => ({ value: position, label: ICON_POSITION_LABELS[position] }))}
                    value={style.iconPosition}
                    onChange={(chosen) => set('iconPosition', chosen as IconPosition | undefined)}
                    isOptional
                    noneLabel={`Default (${ICON_POSITION_LABELS[fallback.iconPosition]})`}
                    placeholder={`Default (${ICON_POSITION_LABELS[fallback.iconPosition]})`}
                />
                {size('iconSize', 'Size')}
                {color('iconColor', 'Colour', 'The title\'s colour when left empty.')}
                {color('iconBackground', 'Background', 'With a background, the size is the background\'s; the icon is smaller inside it.')}
                {size('iconRadius', 'Corner radius')}
                {size('iconMargin', 'Margin')}
            </SettingsGroup>
            <SettingsGroup title="Shadow" note="Drawn outside the tile. Sizes in pixels; the colour with its opacity first (#aarrggbb).">
                <ToggleRow
                    label="Shadow"
                    detail={`Default: ${fallback.shadow ? 'on' : 'off'}`}
                    checked={style.shadow ?? fallback.shadow}
                    // As the capitals: kept only where it differs, so a tile can switch off the dashboard's shadow (DS-5).
                    onChange={(checked) => set('shadow', checked === fallback.shadow ? undefined : checked)}
                />
                {(style.shadow ?? fallback.shadow) ? (
                    <>
                        {color('shadowColor', 'Colour')}
                        {offset('shadowX', 'Across')}
                        {offset('shadowY', 'Down')}
                        {size('shadowBlur', 'Blur')}
                    </>
                ) : null}
            </SettingsGroup>
            <SettingsGroup title="Sizes" note="In pixels. Empty: the default.">
                {SIZES.map(({ key, label }) => size(key, label))}
            </SettingsGroup>
        </>
    );
}
